import React, { useMemo, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DownloadIcon from '@mui/icons-material/Download'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import useAutoCoder from '../hooks/useAutoCoder'
import useSnackbar from '../hooks/useSnackbar'
import { useQueryClient } from '@tanstack/react-query'
import PageHeader from '../components/system/PageHeader'
import AutoCoderStatusChip from '../components/autocoder/AutoCoderStatusChip'
import DemoStepButton from '../components/autocoder/DemoStepButton'
import PipelineSettingsTab from '../components/autocoder/PipelineSettingsTab'
import PipelineThroughputTab from '../components/autocoder/PipelineThroughputTab'
import PipelineImportStatusTab from '../components/autocoder/PipelineImportStatusTab'
import PipelineBatchesSection from '../components/autocoder/pipeline/PipelineBatchesSection'
import RecordsExplorer from '../components/autocoder/RecordsExplorer'
import SuggestionsTable from '../components/autocoder/SuggestionsTable'
import AutoCoderFrameworkOverview from '../components/autocoder/AutoCoderFrameworkOverview'
import { buildColorMap } from '../components/autocoder/colorPalette'
import { API_BASE_URL } from '../constants/system'
import { DataImportStatus, PipelineLifecycle } from '../types/gotypes'
import {
  PipelineSectionId,
  pipelineSections,
  resolvePipelineSection,
} from '../utils/pipelineSections'
import { showsPipelineSwitches } from '../utils/pipelineSwitches'
import { pipelineDisplayStatus } from '../utils/pipelineDisplayStatus'

/**
 * The pipeline PAGE — `/autocoder/:customer/:job/pipelines/:pipelineId`.
 *
 * Replaces the status modal with tabs along the top. Kai: "it feels like we
 * should click on a button and go to a page for the pipeline… possibly with
 * the menu down the left-hand side… I really want to focus on the throughput
 * page." So: left-hand nav, Throughput first and default. The header carries
 * only READ-ONLY state (the coding-gate chip); the switch that changes the
 * gate lives in the Settings section — Kai (2026-08-13): "we should have to
 * go into the pipeline settings in order to change the coding switch".
 *
 * The section lives in the URL (`?section=`) so any view is linkable, and each
 * section is mounted only while selected — the polling views (Throughput, the
 * queue, Imports) must not poll from behind another section.
 */
const SECTION_WIDTH = 220

const AutoCoderPipelinePage: React.FC = () => {
  const router = useRouter()
  const account = useAccount()
  const snackbar = useSnackbar()

  const customer = router.params.customer || account.customer?.name || ''
  const job = router.params.job || account.job?.name || ''
  const pipelineId = router.params.pipelineId || ''

  const autoCoder = useAutoCoder({ customer, job, pipelineId })
  const pipeline = autoCoder.pipeline
  const queryClient = useQueryClient()

  // A demo step has landed its held-back data (Admin → Demos, T20): refetch
  // the pipeline and every AutoCoder view under it, so the page shows it.
  const handleDemoLanded = () => {
    autoCoder.refetch()
    void queryClient.invalidateQueries({
      predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith('autocoder'),
    })
  }

  const section = resolvePipelineSection(router.params.section, pipeline)
  const sections = pipelineSections(pipeline, { suggestions: autoCoder.suggestions.length })

  const [lifecycleMenu, setLifecycleMenu] = useState<null | HTMLElement>(null)
  // Only the lifecycle (pause/activate) is confirmed from this page now — the
  // coding gate's switch and its confirm dialog moved into the Settings
  // section (PipelineSettingsTab), the one place that changes a pipeline.
  const [gateTarget, setGateTarget] = useState<{ next: boolean } | null>(null)

  const colorMap = useMemo(() => {
    const themeFramework = autoCoder.frameworks?.find(fw => fw.name === 'theme')
    const codes = themeFramework?.main_variable?.codes?.map(c => c.code)
    return codes && codes.length > 0 ? buildColorMap(codes) : undefined
  }, [autoCoder.frameworks])

  const goToSection = (next: PipelineSectionId) => router.setParams({ section: next })

  // ---- the lifecycle -----------------------------------------------------
  // Confirm-with-consequence, and "trust the server's effective value": a 200
  // that leaves the lifecycle where it was is a no-op, not a success.
  const lifecycleConsequence = (next: boolean): string =>
    // Importing is NOT in this list on purpose: it is a data-collection
    // concern and the server no longer moves it with the lifecycle, so
    // activating cannot start the scheduled connector loop.
    next
      ? 'Activating the pipeline switches its own gate ON — Coding. ' +
        'Importing is unaffected: it is a data-collection setting and stays exactly as it is.'
      : 'Pausing the pipeline switches its own gate OFF — coding stops. ' +
        'Importing is unaffected, and nothing already collected is lost.'

  const handleConfirmGate = async () => {
    if (!gateTarget || !pipeline) return
    const { next } = gateTarget
    try {
      const res = await autoCoder.setPipelineLifecycle(pipeline.id, next ? 'activate' : 'pause')
      const wanted = next ? PipelineLifecycle.active : PipelineLifecycle.paused
      if (res?.lifecycle !== undefined && res.lifecycle !== wanted) {
        snackbar.warning(
          `Pipeline could not be ${next ? 'activated' : 'paused'} — the server left it ${res.lifecycle}`,
        )
      } else {
        snackbar.success(
          `Pipeline ${next ? 'activated — coding is now ON' : 'paused — coding is now OFF'}`,
        )
      }
    } catch {
      // useAutoCoder already surfaced the error.
    } finally {
      setGateTarget(null)
    }
  }

  const renderSection = () => {
    if (!pipeline) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress color="info" />
        </Box>
      )
    }
    switch (section) {
      case 'throughput':
        return (
          <PipelineThroughputTab customer={customer} job={job} pipelineId={pipeline.id} active />
        )
      // Provenance is job-wide now — a fetch normally belongs to no pipeline at
      // all, so receipts live in the shell's Data tab. What is left here is the
      // pipeline's OWN work: the batches it sent to a model lane.
      case 'batches':
        return (
          <PipelineBatchesSection
            customer={customer}
            job={job}
            pipelineId={pipeline.id}
            colorMap={colorMap}
            active
          />
        )
      // The same shared explorer the receipt and entity drill-downs use, keyed
      // to this pipeline: a record must look identical wherever it is reached
      // from, and one component is how that stays true.
      case 'results':
        return (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <RecordsExplorer
              customer={customer}
              job={job}
              door={{ pipelineId: pipeline.id }}
              colorMap={colorMap}
              evidenceLabel="Match Evidence"
              emptyMessage="No records have been coded by this pipeline yet."
              active
            />
          </Box>
        )
      case 'frameworks':
        return <AutoCoderFrameworkOverview frameworks={autoCoder.frameworks} colorMap={colorMap} />
      case 'suggestions':
        return <SuggestionsTable suggestions={autoCoder.suggestions} />
      case 'settings':
        return <PipelineSettingsTab pipeline={pipeline} customer={customer} job={job} />
      case 'status':
        return <PipelineImportStatusTab customer={customer} job={job} pipelineId={pipeline.id} />
      default:
        return null
    }
  }

  const lifecycle = pipeline?.lifecycle
  const isLongLived = Boolean(pipeline && showsPipelineSwitches(pipeline))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      <PageHeader
        title={pipeline?.config?.name || 'Pipeline'}
        subtitle={
          [
            pipeline?.system_name,
            `${account.customer?.displayName || customer} / ${account.job?.displayName || job}`,
          ].filter(Boolean).join(' · ')
        }
        actions={
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {/* A demo job's staged steps (Managers; nothing on any other job). */}
            <DemoStepButton customer={customer} job={job} onLanded={handleDemoLanded} />
            {/* Read-only: the chip folds in the coding gate ('coding on/off')
                and failure precedence. The switch that CHANGES the gate lives
                in the Settings section — deliberately not up here, so state
                and control don't sit side by side looking identical. */}
            {pipeline && (
              <AutoCoderStatusChip
                state={pipelineDisplayStatus(pipeline)}
                error={pipeline.data_error || pipeline.coding_error}
              />
            )}
            {pipeline?.data_status === DataImportStatus.ready && (
              <Tooltip title="Download raw CSV">
                <IconButton
                  color="info"
                  aria-label="Download raw CSV"
                  onClick={() => {
                    if (!account.user) return
                    window.open(
                      `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${pipeline.id}/raw-csv?access_token=${account.user.token}`,
                      '_blank',
                    )
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Pipeline actions">
              <IconButton aria-label="Pipeline actions" onClick={e => setLifecycleMenu(e.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Back to pipelines">
              <IconButton
                aria-label="Back to pipelines"
                onClick={() => router.navigate('autocoder', { customer, job })}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      <Menu anchorEl={lifecycleMenu} open={Boolean(lifecycleMenu)} onClose={() => setLifecycleMenu(null)}>
        {isLongLived && lifecycle !== PipelineLifecycle.archived && (
          <MenuItem
            onClick={() => {
              setLifecycleMenu(null)
              setGateTarget({ next: lifecycle !== PipelineLifecycle.active })
            }}
          >
            {lifecycle === PipelineLifecycle.active ? 'Pause pipeline' : 'Activate pipeline'}
          </MenuItem>
        )}
        {/*
          "Edit pipeline settings" used to live here and opened the CREATE
          wizard on its own route — a second, differently-shaped view of the
          same object, whose back button returned to the pipelines list. The
          Settings section below now holds every editable setting, so this menu
          is left to lifecycle actions and the entry points at the section.
        */}
        <MenuItem
          onClick={() => {
            setLifecycleMenu(null)
            goToSection('settings')
          }}
        >
          Pipeline settings
        </MenuItem>
      </Menu>

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <Box
          component="nav"
          aria-label="Pipeline sections"
          sx={{
            width: SECTION_WIDTH,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
            overflowY: 'auto',
            bgcolor: 'background.default',
          }}
        >
          {/* Lifecycle lives here as page state — the header chip carries the
              coding gate, which is a different axis. */}
          {isLongLived && (
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em' }}>
                Pipeline state
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: lifecycle === PipelineLifecycle.active ? 'info.main' : 'warning.main',
                }}
              >
                {lifecycle === PipelineLifecycle.active
                  ? 'Active'
                  : lifecycle === PipelineLifecycle.archived
                    ? 'Archived'
                    : 'Paused'}
              </Typography>
            </Box>
          )}
          <List dense sx={{ py: 1 }}>
            {sections.map(s => (
              <ListItemButton
                key={s.id}
                selected={s.id === section}
                onClick={() => goToSection(s.id)}
                sx={{
                  borderLeft: '3px solid',
                  borderColor: s.id === section ? 'info.main' : 'transparent',
                  '&.Mui-selected': { bgcolor: 'action.selected' },
                }}
              >
                <ListItemText
                  primary={s.label}
                  primaryTypographyProps={{
                    fontWeight: s.id === section ? 700 : 500,
                    fontSize: '0.875rem',
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          {pipeline?.data_status === 'failed' && pipeline?.data_error && (
            <Alert severity="error" sx={{ m: 2 }}>
              <AlertTitle>Data Import Failed</AlertTitle>
              {pipeline.data_error}
            </Alert>
          )}
          {pipeline?.coding_status === 'failed' && pipeline?.coding_error && (
            <Alert severity="error" sx={{ m: 2 }}>
              <AlertTitle>Coding Failed</AlertTitle>
              {pipeline.coding_error}
            </Alert>
          )}
          {renderSection()}
        </Box>
      </Box>

      <Dialog open={Boolean(gateTarget)} onClose={() => setGateTarget(null)}>
        <DialogTitle>
          {gateTarget ? (gateTarget.next ? 'Activate pipeline' : 'Pause pipeline') : ''}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {gateTarget
              ? `"${pipeline?.config?.name || pipelineId}" — ${lifecycleConsequence(gateTarget.next)}`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGateTarget(null)}>Cancel</Button>
          <Button
            color="info"
            onClick={handleConfirmGate}
            disabled={autoCoder.setPipelineLifecycleLoading}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AutoCoderPipelinePage
